import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  ClickAwayListener,
  Typography,
} from '@mui/material';

import {
  BarChartRounded,
  FactoryRounded,
  RefreshRounded,
  ReportProblemRounded,
} from '@mui/icons-material';

import {
  getIndustrialCostPlantDashboard,
} from '../services/plantDashboardService';

import type {
  PlantDashboardLine,
} from '../services/plantDashboardService';

import {
  getIndustrialCostMaterialStockProjection,
  getWasteControl,
  getPlantPanelProduction,
  listIndustrialCostProductionLinesConfig,
} from '../services/industrialCostService';

import type {
  MaterialStockProjectionRow,
  ProductionLineConfig,
  PanelProductionRow,
} from '../types/industrialCost';

import IndustrialCostMaterialCoverageDialog from './IndustrialCostMaterialCoverageDialog';
import IndustrialCostCoverageTable from './IndustrialCostCoverageTable';
import IndustrialCostMaterialRotationSection from './IndustrialCostMaterialRotationSection';
import IndustrialCostWasteView, { WASTE_QUERY_KEY } from './IndustrialCostWasteView';
import {
  PLANT_DATA_FRESHNESS_QUERY_KEY,
  PlantDataFreshnessButton,
  PlantDataFreshnessDialog,
  PlantDataNoticesDialog,
  usePlantDataNotices,
} from './IndustrialCostPlantDataFreshnessStrip';
import { PLANT_DATA_REVIEW_QUERY_KEY } from './IndustrialCostFileReviewPanel';

import { esNumberFormat } from '../utils/numberFormat';
import {
  buildLineStats,
  compareWithPreviousYear,
  groupNoticesByType,
  orderDisplayDate,
  sortOrdersBy,
  sortOrdersForCard,
  workloadColor,
} from '../utils/plantPanel';
import type { LineStats, OrderSortKey } from '../utils/plantPanel';
import { defaultWasteYear, wasteButtonSummary } from '../utils/wasteControl';
import { useTableSort } from '../utils/tableSort';
import SortableHeaderCell from '../components/SortableHeaderCell';

const PANEL = {
  bgcolor: '#151f30',
  border: '1px solid rgba(148,163,184,0.15)',
  borderRadius: 3,
  boxShadow: 'none',
};

const MUTED = '#94a3b8';
const BLUE = '#38bdf8';

const numberFormat = esNumberFormat({
  maximumFractionDigits: 1,
});

const WASTE_PARAM = 'panel';
const WASTE_VALUE = 'desperdicio';
const wastePctFormat = esNumberFormat({ minimumFractionDigits: 2, maximumFractionDigits: 2 });

function fmt(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }

  return numberFormat.format(value);
}

function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';

  const parsed = new Date(`${value.slice(0, 10)}T12:00:00`);

  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleDateString('es-ES');
}

function machineGroup(machine: string): string {
  const name = machine.toUpperCase();

  if (name.includes('EPDM')) return 'EPDM';
  if (name.includes('TPE')) return 'TPE';
  if (name.includes('COEX')) return 'COEXTRUSIÓN';
  if (name.includes('CARTON') || name.includes('CARTÓN')) return 'CARTÓN';

  return 'OTRAS';
}

function materialRequirementGroups(
  item: MaterialStockProjectionRow,
  productionLines: ProductionLineConfig[],
): string[] {
  return Array.from(
    new Set(
      item.requirement_events
        .map((event) => event.machine?.trim())
        .filter((machine): machine is string => Boolean(machine))
        .map((machine) =>
          dashboardMachineGroup(
            machine,
            productionLines,
          ),
        ),
    ),
  ).sort();
}

function sumProduction(
  rows: PanelProductionRow[],
  field: 'kg_produced' | 'units_produced',
): number | null {
  const valid = rows.filter((row) => row[field] !== null);

  if (!valid.length) return null;

  return valid.reduce(
    (total, row) => total + Number(row[field] ?? 0),
    0,
  );
}

type ProductionKpiGranularity = 'day' | 'month' | 'year';

type ProductionKpiStats = {
  kg: number | null;
  meters: number | null;
  previousKg: number | null;
  previousMeters: number | null;
  granularity: ProductionKpiGranularity | null;
  periodLabel: string;
};

function buildProductionKpiStats(
  rows: PanelProductionRow[],
  currentYear: number,
): ProductionKpiStats {
  const previousYear = currentYear - 1;

  const currentDaily = rows.filter(
    (row) =>
      row.source_granularity === 'day' &&
      Boolean(row.production_date) &&
      row.production_date?.startsWith(String(currentYear)),
  );

  if (currentDaily.length > 0) {
    const dates = currentDaily
      .map((row) => row.production_date as string)
      .sort();

    const firstDate = dates[0];
    const lastDate = dates[dates.length - 1];

    const previousDaily = rows.filter(
      (row) =>
        row.source_granularity === 'day' &&
        Boolean(row.production_date) &&
        row.production_date?.startsWith(String(previousYear)) &&
        row.production_date!.slice(5) >= firstDate.slice(5) &&
        row.production_date!.slice(5) <= lastDate.slice(5),
    );

    return {
      kg: sumProduction(currentDaily, 'kg_produced'),
      meters: sumProduction(currentDaily, 'units_produced'),
      previousKg: sumProduction(previousDaily, 'kg_produced'),
      previousMeters: sumProduction(previousDaily, 'units_produced'),
      granularity: 'day',
      periodLabel: `${fmtDate(firstDate)} – ${fmtDate(lastDate)}`,
    };
  }

  const currentMonthly = rows.filter(
    (row) =>
      row.source_granularity === 'month' &&
      row.period_year === currentYear &&
      row.period_month !== null,
  );

  if (currentMonthly.length > 0) {
    const months = Array.from(
      new Set(
        currentMonthly
          .map((row) => row.period_month)
          .filter((month): month is number => month !== null),
      ),
    ).sort((a, b) => a - b);

    const previousMonthly = rows.filter(
      (row) =>
        row.source_granularity === 'month' &&
        row.period_year === previousYear &&
        row.period_month !== null &&
        months.includes(row.period_month),
    );

    const monthLabel =
      months.length === 1
        ? `mes ${months[0]}`
        : `meses ${months[0]}–${months[months.length - 1]}`;

    return {
      kg: sumProduction(currentMonthly, 'kg_produced'),
      meters: sumProduction(currentMonthly, 'units_produced'),
      previousKg: sumProduction(previousMonthly, 'kg_produced'),
      previousMeters: sumProduction(previousMonthly, 'units_produced'),
      granularity: 'month',
      periodLabel: monthLabel,
    };
  }

  const currentAnnual = rows.filter(
    (row) =>
      row.source_granularity === 'year' &&
      row.period_year === currentYear,
  );

  if (currentAnnual.length > 0) {
    const previousAnnual = rows.filter(
      (row) =>
        row.source_granularity === 'year' &&
        row.period_year === previousYear,
    );

    return {
      kg: sumProduction(currentAnnual, 'kg_produced'),
      meters: sumProduction(currentAnnual, 'units_produced'),
      previousKg: sumProduction(previousAnnual, 'kg_produced'),
      previousMeters: sumProduction(previousAnnual, 'units_produced'),
      granularity: 'year',
      periodLabel: `ejercicio ${currentYear}`,
    };
  }

  return {
    kg: null,
    meters: null,
    previousKg: null,
    previousMeters: null,
    granularity: null,
    periodLabel: 'sin producción comparable cargada',
  };
}

function normalizeProductionLineRef(
  value: string | null | undefined,
): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

function resolveDashboardLineConfig(
  machine: string,
  productionLines: ProductionLineConfig[],
): ProductionLineConfig | null {
  const target = normalizeProductionLineRef(machine);

  if (!target) return null;

  return (
    productionLines.find((line) =>
      [line.code, line.name, ...(line.aliases ?? [])].some(
        (candidate) =>
          normalizeProductionLineRef(candidate) === target,
      ),
    ) ?? null
  );
}

function resolveDashboardLineId(
  machine: string,
  productionLines: ProductionLineConfig[],
): string | null {
  return (
    resolveDashboardLineConfig(
      machine,
      productionLines,
    )?.id ?? null
  );
}

function dashboardMachineGroup(
  machine: string,
  productionLines: ProductionLineConfig[],
): string {
  const configuredLine = resolveDashboardLineConfig(
    machine,
    productionLines,
  );

  const configuredGroup = String(
    configuredLine?.production_line_group_code ??
      configuredLine?.production_line_group_name ??
      configuredLine?.process_group ??
      '',
  )
    .trim()
    .toUpperCase();

  return configuredGroup || machineGroup(machine);
}

// IC-PD-01: KPIs de producción con comparación homogénea.

// IND-015: comparación con el mismo periodo del año anterior, con el aspecto
// de la vista general del Margin Center (copiado aquí; no se importa nada de
// PROA · Pricing): «↑ +x %» verde, «↓ −x %» rojo, «→» gris.
const TREND_STYLE = {
  up: { arrow: '↑', color: '#4ade80' },
  down: { arrow: '↓', color: '#f87171' },
  flat: { arrow: '→', color: MUTED },
} as const;

const pctFormat = esNumberFormat({ minimumFractionDigits: 1, maximumFractionDigits: 1 });

function YearTrend({
  current,
  previous,
  previousYear,
  unit,
  comparable = true,
}: {
  current: number | null | undefined;
  previous: number | null | undefined;
  previousYear: number;
  unit: string;
  comparable?: boolean;
}) {
  const result = comparable
    ? compareWithPreviousYear(current, previous)
    : { direction: 'flat' as const, pct: null };
  const style = TREND_STYLE[result.direction];
  const pctText =
    result.pct === null
      ? 'sin periodo comparable'
      : `${result.pct > 0 ? '+' : result.pct < 0 ? '−' : ''}${pctFormat.format(Math.abs(result.pct))} %`;
  return (
    <Typography component="span" sx={{ fontSize: 11, lineHeight: 1.3, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
      <Box component="span" sx={{ color: style.color, fontWeight: 800 }}>
        {style.arrow} {pctText}
      </Box>
      {result.pct !== null && previous != null ? ` · ${previousYear}: ${fmt(previous)}${unit ? ` ${unit}` : ''}` : ''}
    </Typography>
  );
}

function Kpi({
  title,
  value,
  subtitle,
  trend,
}: {
  title: string;
  value: string;
  subtitle: string;
  trend?: React.ReactNode;
}) {
  return (
    <Paper
      sx={{
        ...PANEL,
        px: 1.5,
        py: 1.15,
        minWidth: 0,
        borderRadius: 2,
      }}
    >
      <Typography
        sx={{
          color: MUTED,
          fontSize: 10,
          fontWeight: 850,
          letterSpacing: 0.35,
          textTransform: 'uppercase',
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          mt: 0.3,
          color: '#f8fafc',
          fontWeight: 950,
          fontSize: { xs: 20, md: 25 },
          lineHeight: 1.15,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>

      {trend ? (
        <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid rgba(148,163,184,0.14)' }}>{trend}</Box>
      ) : null}

      {subtitle ? (
        <Typography
          sx={{
            display: 'block',
            mt: 0.35,
            color: MUTED,
            fontSize: 10.5,
            lineHeight: 1.25,
          }}
        >
          {subtitle}
        </Typography>
      ) : null}
    </Paper>
  );
}


// IND-015: botón «Estadísticas» de la tarjeta. Se despliega al pasar el ratón
// y también al pulsar (pantallas táctiles).
function LineStatsButton({
  stats,
  producedKg,
  currentYear,
}: {
  stats: LineStats | undefined;
  producedKg: number | null;
  currentYear: number;
}) {
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  const previousYear = currentYear - 1;
  const rows: Array<{ label: string; value: string; trend?: React.ReactNode }> = [
    {
      label: `Metros fabricados ${currentYear}`,
      value: stats?.currentMeters == null ? '—' : `${fmt(stats.currentMeters)} m`,
      trend: <YearTrend current={stats?.currentMeters} previous={stats?.previousMeters} previousYear={previousYear} unit="m" />,
    },
    {
      label: 'Ritmo (últimas 8 semanas)',
      value: stats?.recentWeeklyMeters == null ? '—' : `${fmt(stats.recentWeeklyMeters)} m/sem`,
      trend: (
        <YearTrend
          current={stats?.recentWeeklyMeters}
          previous={stats?.previousRecentWeeklyMeters}
          previousYear={previousYear}
          unit="m/sem"
        />
      ),
    },
    { label: `Kg fabricados ${currentYear}`, value: producedKg == null ? '—' : `${fmt(producedKg)} kg` },
    {
      label: `Días con producción ${currentYear}`,
      value: stats?.productionDays == null ? '—' : fmt(stats.productionDays),
    },
  ];
  const content = (
    <Stack spacing={1} sx={{ p: 0.5, minWidth: 240 }}>
      {rows.map((row) => (
        <Box key={row.label}>
          <Typography sx={{ color: MUTED, fontSize: 11 }}>{row.label}</Typography>
          <Typography sx={{ color: '#f8fafc', fontSize: 14, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
            {row.value}
          </Typography>
          {row.trend ?? null}
        </Box>
      ))}
    </Stack>
  );
  return (
    <ClickAwayListener onClickAway={() => setPinned(false)}>
      <Box component="span">
        <Tooltip
          open={hover || pinned}
          onOpen={() => setHover(true)}
          onClose={() => setHover(false)}
          title={content}
          placement="bottom-end"
          slotProps={{ tooltip: { sx: { bgcolor: '#0f172a', border: '1px solid rgba(148,163,184,0.25)', maxWidth: 320 } } }}
        >
          <Button
            size="small"
            variant="text"
            startIcon={<BarChartRounded sx={{ fontSize: 16 }} />}
            onClick={() => setPinned((current) => !current)}
            aria-expanded={hover || pinned}
            sx={{ textTransform: 'none', py: 0, minWidth: 0, fontSize: 12 }}
          >
            Estadísticas
          </Button>
        </Tooltip>
      </Box>
    </ClickAwayListener>
  );
}

const ORDER_DATE_COLOR = {
  late: '#fdba74',
  normal: MUTED,
  theoretical: '#64748b',
  none: '#64748b',
} as const;

function todayIsoDate(): string {
  const todayDate = new Date();
  return [
    todayDate.getFullYear(),
    String(todayDate.getMonth() + 1).padStart(2, '0'),
    String(todayDate.getDate()).padStart(2, '0'),
  ].join('-');
}

function LineCard({
  line,
  stats,
  currentYear,
  onOpenOrders,
}: {
  line: PlantDashboardLine;
  stats: LineStats | undefined;
  currentYear: number;
  onOpenOrders: () => void;
}) {
  // IND-015: días de carga frente al plazo de entrega (verde ≤ 75 %, naranja
  // > 75 % y ≤ 100 %, rojo por encima). Escala 0–1,5 × plazo con su marca.
  const targetDays = line.workload_target_working_days || 0;
  const workloadDays = line.workload_days;
  const ratio =
    workloadDays !== null && workloadDays !== undefined && targetDays > 0
      ? workloadDays / targetDays
      : null;
  const color = workloadColor(ratio, MUTED);
  const barWidth = ratio === null ? 0 : Math.min(ratio / 1.5, 1) * 100;
  const todayIso = todayIsoDate();
  const orders = sortOrdersForCard(line.orders, todayIso);

  return (
    <Paper
      component="article"
      sx={{
        ...PANEL,
        p: 2,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Typography sx={{ fontWeight: 800, color: '#f8fafc', fontSize: 17 }}>
          {line.machine}
        </Typography>
        <LineStatsButton stats={stats} producedKg={line.produced_material_kg} currentYear={currentYear} />
      </Stack>

      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <Typography sx={{ fontSize: 30, fontWeight: 800, color, fontVariantNumeric: 'tabular-nums' }}>
            {workloadDays === null || workloadDays === undefined ? '—' : fmt(workloadDays)}{' '}
            <Box component="span" sx={{ fontSize: 14, fontWeight: 600, color: MUTED }}>
              días de carga
            </Box>
          </Typography>
          <Typography sx={{ color: MUTED, fontSize: 12 }}>
            plazo {targetDays ? fmt(targetDays) : '—'} días
          </Typography>
        </Stack>
        <Box
          role="img"
          aria-label={`Carga frente al plazo: ${ratio === null ? 'sin dato' : `${Math.round(ratio * 100)} %`}`}
          sx={{ position: 'relative', height: 8, borderRadius: 999, bgcolor: 'rgba(148,163,184,0.18)', mt: 0.75 }}
        >
          <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${barWidth}%`, borderRadius: 999, bgcolor: color }} />
          <Box sx={{ position: 'absolute', left: '66.7%', top: -3, bottom: -3, width: 2, bgcolor: '#f8fafc' }} />
        </Box>
        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Typography sx={{ color: MUTED, fontSize: 12 }}>Metros pendientes</Typography>
            <Typography sx={{ fontWeight: 700, color: '#f8fafc', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
              {fmt(line.pending_m)} m
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ color: MUTED, fontSize: 12 }}>Kg pendientes</Typography>
            <Typography sx={{ fontWeight: 700, color: '#f8fafc', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
              {line.pending_material_kg === null ? '—' : `${fmt(line.pending_material_kg)} kg`}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {line.orders_without_pending_hours > 0 ? (
        <Typography sx={{ fontSize: 12, color: '#fcd34d' }}>
          Carga incompleta: {fmt(line.orders_without_pending_hours)} OF sin horas
        </Typography>
      ) : null}

      <Box sx={{ borderTop: '1px solid rgba(148,163,184,0.14)', pt: 1.25, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ color: MUTED, fontSize: 13 }}>
          <span>Próximas expediciones · {fmt(line.orders_count)} OF</span>
          {line.orders_count > 0 ? <span>m</span> : null}
        </Stack>
        {line.orders_count > 0 ? (
          <>
            {/* 5 filas visibles y scroll interno */}
            <Stack spacing={0.5} sx={{ maxHeight: 5 * 24, overflowY: 'auto', pr: 0.5 }}>
              {orders.map((order) => {
                const date = orderDisplayDate(order, todayIso);
                return (
                  <Stack
                    key={order.manufacturing_order_number}
                    direction="row"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ minHeight: 20 }}
                  >
                    <Typography noWrap sx={{ color: '#e2e8f0', fontSize: 13 }}>
                      {order.product_ref || order.manufacturing_order_number || '—'}{' '}
                      <Box component="span" sx={{ color: ORDER_DATE_COLOR[date.tone] }}>
                        · {date.day ? fmtDate(date.day) : 'sin fecha'}
                      </Box>
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                      {fmt(order.pending_m)}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
            <Button
              size="small"
              variant="text"
              onClick={onOpenOrders}
              sx={{ alignSelf: 'flex-start', px: 0, textTransform: 'none' }}
            >
              Ver las {fmt(line.orders_count)} OF
            </Button>
          </>
        ) : (
          <Typography sx={{ color: MUTED, fontSize: 13 }}>
            Sin OF pendientes en el fichero vigente.
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

const ORDER_COLUMNS: Array<{ key: OrderSortKey; label: string; align?: 'right' }> = [
  { key: 'manufacturing_order_number', label: 'OF' },
  { key: 'product_ref', label: 'Referencia' },
  { key: 'pending_m', label: 'Metros', align: 'right' },
  { key: 'dispatch_date', label: 'Expedición' },
  { key: 'customer_number', label: 'Cliente' },
];

type IndustrialCostPlantDashboardViewProps = {
  onOpenFileReview?: () => void;
};

export default function IndustrialCostPlantDashboardView({
  onOpenFileReview,
}: IndustrialCostPlantDashboardViewProps = {}) {
  const [selectedGroup, setSelectedGroup] = useState('EPDM');
  const queryClient = useQueryClient();

  // IND-033: vista «Desperdicio» con su propia dirección, para que el «Atrás»
  // del navegador vuelva al Panel.
  const [wasteOpen, setWasteOpen] = useState(
    () => new URLSearchParams(window.location.search).get(WASTE_PARAM) === WASTE_VALUE,
  );
  const wastePushed = useRef(false);
  useEffect(() => {
    const onPopState = () => {
      wastePushed.current = false;
      setWasteOpen(new URLSearchParams(window.location.search).get(WASTE_PARAM) === WASTE_VALUE);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  const openWaste = () => {
    const url = new URL(window.location.href);
    url.searchParams.set(WASTE_PARAM, WASTE_VALUE);
    window.history.pushState(window.history.state, '', url);
    wastePushed.current = true;
    setWasteOpen(true);
  };
  const closeWaste = () => {
    if (wastePushed.current) {
      window.history.back();
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.delete(WASTE_PARAM);
    window.history.replaceState(window.history.state, '', url);
    setWasteOpen(false);
  };
  const wasteQuery = useQuery({
    queryKey: [...WASTE_QUERY_KEY, defaultWasteYear()],
    queryFn: () => getWasteControl(defaultWasteYear()),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const wasteButton = wasteButtonSummary(wasteQuery.data);

  const [selectedLine, setSelectedLine] =
    useState<PlantDashboardLine | null>(null);

  const [dataQualityDialogOpen, setDataQualityDialogOpen] =
    useState(false);

  const [freshnessDialogOpen, setFreshnessDialogOpen] =
    useState(false);

  const ordersSort = useTableSort<OrderSortKey>('dispatch_date', 'asc');


  const [
    selectedMaterialCoverage,
    setSelectedMaterialCoverage,
  ] = useState<MaterialStockProjectionRow | null>(null);

  const overviewQuery = useQuery({
    queryKey: ['industrial-cost-plant-dashboard'],
    queryFn: getIndustrialCostPlantDashboard,
    staleTime: 60_000,
  });

  const stockQuery = useQuery({
    queryKey: ['industrial-cost-material-stock-projection'],
    queryFn: getIndustrialCostMaterialStockProjection,
    staleTime: 60_000,
  });

  const productionQuery = useQuery({
    queryKey: ['industrial-cost-production-volumes', 'panel'],
    queryFn: getPlantPanelProduction,
    staleTime: 60_000,
  });

  const productionLinesQuery = useQuery({
    queryKey: ['industrial-cost-production-config-lines'],
    queryFn: listIndustrialCostProductionLinesConfig,
    staleTime: 60_000,
  });

  // IND-012: avisos de datos (sustituye a «Incidencias de datos»).
  const noticesQuery = usePlantDataNotices();
  // IND-015: el botón cuenta tipos de aviso, no avisos sueltos.
  const noticesCount = groupNoticesByType(noticesQuery.data?.notices ?? []).length;
  const blockingNoticesCount =
    noticesQuery.data?.notices?.filter((notice) => notice.level === 'blocks').length ?? 0;

  const overview = overviewQuery.data;

  const currentYear = new Date().getFullYear();

  // Se usa una única granularidad homogénea para cada comparación:
  // diario > mensual > anual. Nunca se suman entre sí granularidades distintas.
  const productionStats = useMemo(
    () =>
      buildProductionKpiStats(
        productionQuery.data ?? [],
        currentYear,
      ),
    [productionQuery.data, currentYear],
  );

  // IND-015: el texto técnico de los indicadores va en la ventana «Datos a fecha».
  const kpiNote = `Producción usada en KPIs: ${
    productionStats.granularity === 'day'
      ? 'detalle diario'
      : productionStats.granularity === 'month'
        ? 'agregado mensual'
        : productionStats.granularity === 'year'
          ? 'agregado anual'
          : 'sin datos'
  } · ${productionStats.periodLabel}. La comparación usa la misma granularidad del año anterior y no mezcla agregados incompatibles.`;

  // IND-016: los registros sin convertir salen de la tarjeta de kg y van aquí.
  const kgNote = overview
    ? `Kg fabricados: materia prima según escandallo · ${fmt(overview.summary.produced_material_kg_rows_unconverted ?? 0)} registros de ${currentYear} sin convertir · ${fmt(overview.summary.previous_produced_material_kg_rows_unconverted ?? 0)} registros de ${currentYear - 1} sin convertir.`
    : null;

  const lines = overview?.lines ?? [];

  const lineProductionStatsByMachine = useMemo(() => {
    const productionRows = productionQuery.data ?? [];
    const productionLines = productionLinesQuery.data ?? [];

    return Object.fromEntries(
      lines.map((line) => {
        const productionLineConfigId = resolveDashboardLineId(
          line.machine,
          productionLines,
        );

        return [
          line.machine,
          buildLineStats(
            productionRows,
            currentYear,
            productionLineConfigId,
          ),
        ];
      }),
    ) as Record<string, LineStats>;
  }, [
    lines,
    productionQuery.data,
    productionLinesQuery.data,
    currentYear,
  ]);

  const groups = useMemo(() => {
    const productionLines =
      productionLinesQuery.data ?? [];

    return [
      'TODAS',
      ...Array.from(
        new Set(
          lines.map((line) =>
            dashboardMachineGroup(
              line.machine,
              productionLines,
            ),
          ),
        ),
      ),
    ];
  }, [lines, productionLinesQuery.data]);

  const visibleLines = useMemo(() => {
    const productionLines =
      productionLinesQuery.data ?? [];

    return lines.filter(
      (line) =>
        selectedGroup === 'TODAS' ||
        dashboardMachineGroup(
          line.machine,
          productionLines,
        ) === selectedGroup,
    );
  }, [
    lines,
    selectedGroup,
    productionLinesQuery.data,
  ]);

  const rawMaterialStockRows = useMemo(() => {
    const productionLines =
      productionLinesQuery.data ?? [];

    return [...(stockQuery.data?.rows ?? [])]
      .filter((item) => {
        const isRawMaterial =
          String(item.material_type ?? '')
            .trim()
            .toUpperCase() === 'MP' &&
          !item.packaging_type;

        if (!isRawMaterial) {
          return false;
        }

        // IND-009: con stock negativo sale aunque no tenga necesidad.
        if (
          item.calculated_stock_qty != null &&
          item.calculated_stock_qty < 0 &&
          item.pending_requirement_qty <= 0
        ) {
          return true;
        }

        if (item.pending_requirement_qty <= 0) {
          return false;
        }

        if (selectedGroup === 'TODAS') {
          return true;
        }

        return materialRequirementGroups(
          item,
          productionLines,
        ).includes(selectedGroup);
      })
      .sort(
        (a, b) =>
          b.pending_requirement_qty -
          a.pending_requirement_qty,
      );
  }, [
    stockQuery.data,
    selectedGroup,
    productionLinesQuery.data,
  ]);

  const packagingStockRows = useMemo(() => {
    const productionLines =
      productionLinesQuery.data ?? [];

    return [...(stockQuery.data?.rows ?? [])]
      .filter((item) => {
        const isPackaging =
          item.packaging_type === 'O' ||
          item.packaging_type === 'CAJA';

        if (!isPackaging) {
          return false;
        }

        // IND-009: con stock negativo sale aunque no tenga necesidad.
        if (
          item.calculated_stock_qty != null &&
          item.calculated_stock_qty < 0 &&
          item.pending_requirement_qty <= 0
        ) {
          return true;
        }

        if (item.pending_requirement_qty <= 0) {
          return false;
        }

        if (selectedGroup === 'TODAS') {
          return true;
        }

        return materialRequirementGroups(
          item,
          productionLines,
        ).includes(selectedGroup);
      })
      .sort(
        (a, b) =>
          b.pending_requirement_qty -
          a.pending_requirement_qty,
      );
  }, [
    stockQuery.data,
    selectedGroup,
    productionLinesQuery.data,
  ]);

  const refreshAll = () => {
    void queryClient.invalidateQueries({ queryKey: PLANT_DATA_FRESHNESS_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: PLANT_DATA_REVIEW_QUERY_KEY });
    void overviewQuery.refetch();
    void stockQuery.refetch();
    void productionQuery.refetch();
    void productionLinesQuery.refetch();
  };

  // IC-STOCK-01A: diagnóstico descargable de stock. Solo lectura.
  // Se exportan TODOS los materiales recibidos del backend, sin los filtros
  // visuales EPDM/TP ni la exclusión de materiales sin necesidades.
  const handleDownloadStockAudit = () => {
    if (!stockQuery.data) return;

    const now = new Date().toISOString();
    const content = JSON.stringify(
      {
        audit_version: 'IC-STOCK-01A',
        exported_at: now,
        source: '/industrial-costs/material-stock-projection',
        projection: stockQuery.data,
      },
      null,
      2,
    );
    const blob = new Blob([content], {
      type: 'application/json;charset=utf-8',
    });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `proa_auditoria_stock_${now.slice(0, 10)}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  };

  const isLoading =
    overviewQuery.isLoading ||
    stockQuery.isLoading ||
    productionQuery.isLoading ||
    productionLinesQuery.isLoading;

  if (wasteOpen) return <IndustrialCostWasteView onBack={closeWaste} />;

  return (
    <Stack spacing={1.25} sx={{ pb: 3, minWidth: 0 }}>
      {/* CABECERA */}
      <Paper
        sx={{
          ...PANEL,
          px: 2,
          py: 1.25,
          borderRadius: 2,
        }}
      >

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{ color: BLUE, fontWeight: 900, letterSpacing: 1.2 }}
            >
              PROA · INDUSTRIAL
            </Typography>

            <Typography
              variant="h5"
              sx={{
                color: '#f8fafc',
                fontWeight: 950,
                fontSize: { xs: 19, md: 23 },
                lineHeight: 1.15,
              }}
            >
              Panel de planta
            </Typography>

            <Typography
              sx={{
                color: MUTED,
                mt: 0.25,
                fontSize: 11.5,
              }}
            >
              Producción, carga de trabajo, stocks y entregas pendientes.
            </Typography>

          </Box>

          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
          >
            <PlantDataFreshnessButton onClick={() => setFreshnessDialogOpen(true)} />

            <Button
              variant="outlined"
              startIcon={
                noticesQuery.isFetching ? (
                  <CircularProgress size={15} thickness={5} />
                ) : (
                  <ReportProblemRounded />
                )
              }
              onClick={() => setDataQualityDialogOpen(true)}
              sx={{
                textTransform: 'none',
                borderColor:
                  blockingNoticesCount > 0
                    ? 'rgba(248,113,113,0.55)'
                    : noticesCount > 0
                      ? 'rgba(245,158,11,0.45)'
                      : undefined,
                color:
                  blockingNoticesCount > 0
                    ? '#fca5a5'
                    : noticesCount > 0
                      ? '#fde68a'
                      : undefined,
              }}
            >
              Avisos de datos ·{' '}
              {noticesQuery.isLoading ? '…' : noticesCount}
            </Button>

            <Button
              variant="outlined"
              startIcon={<RefreshRounded />}
              onClick={refreshAll}
              sx={{ textTransform: 'none' }}
            >
              Actualizar
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {isLoading ? (
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <CircularProgress size={19} />
          <Typography sx={{ color: MUTED }}>
            Cargando información de planta...
          </Typography>
        </Stack>
      ) : null}

      {overviewQuery.isError ? (
        <Alert severity="error">
          No se ha podido cargar el dashboard de OF pendientes.
          Comprueba que el nuevo endpoint esté desplegado.
        </Alert>
      ) : null}

      {!overviewQuery.isLoading && !overview?.snapshot ? (
        <Alert severity="warning">
          No hay ningún snapshot de OF publicado disponible.
        </Alert>
      ) : null}

      {/* INDICADORES SUPERIORES */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 1.5,
        }}
      >
        <Kpi
          title={`KG FABRICADOS · ${currentYear}`}
          value={
            overview?.summary.produced_material_kg === null ||
            overview?.summary.produced_material_kg === undefined
              ? '—'
              : fmt(overview.summary.produced_material_kg)
          }
          trend={
            <YearTrend
              current={overview?.summary.produced_material_kg}
              previous={overview?.summary.previous_produced_material_kg}
              previousYear={currentYear - 1}
              unit="kg"
              comparable={
                Boolean(overview?.summary.produced_material_kg_granularity) &&
                overview?.summary.previous_produced_material_kg_granularity ===
                  overview?.summary.produced_material_kg_granularity
              }
            />
          }
          subtitle={
            overview && overview.summary.material_mass_categories_included === 0
              ? 'Configura qué clasificaciones cuentan como materia prima'
              : ''
          }
        />

        <Kpi
          title={`METROS FABRICADOS · ${currentYear}`}
          value={
            productionStats.meters === null
              ? '—'
              : fmt(productionStats.meters)
          }
          trend={
            <YearTrend
              current={productionStats.meters}
              previous={productionStats.previousMeters}
              previousYear={currentYear - 1}
              unit="m"
            />
          }
          subtitle=""
        />

        <Kpi
          title="MATERIALES CON STOCK CALCULADO"
          value={fmt(
            stockQuery.data?.summary.materials_with_calculated_stock,
          )}
          subtitle="Stock teórico, no recuento físico de hoy"
        />

        <Kpi
          title="CARGA TOTAL PENDIENTE"
          value={`${fmt(overview?.summary.pending_meters)} m`}
          subtitle={`${fmt(overview?.summary.orders_pending)} OF con fabricación pendiente`}
        />
      </Box>

      {/* FILTROS POR FAMILIA */}
      <Paper
        sx={{
          ...PANEL,
          px: 1.5,
          py: 0.85,
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1.5}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
          >
            <FactoryRounded sx={{ color: BLUE, fontSize: 19 }} />

            <Typography
              sx={{
                fontWeight: 900,
                color: '#f8fafc',
                fontSize: 12,
                whiteSpace: 'nowrap',
              }}
            >
              Carga de trabajo por línea
            </Typography>
          </Stack>

          <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center">
            <Typography sx={{ color: MUTED, fontSize: 13 }}>Mostrando:</Typography>
            {groups.map((group) => (
              <Chip
                key={group}
                label={group}
                clickable
                onClick={() => setSelectedGroup(group)}
                aria-pressed={selectedGroup === group}
                variant={
                  selectedGroup === group ? 'filled' : 'outlined'
                }
                color={
                  selectedGroup === group ? 'info' : 'default'
                }
                sx={{
                  fontWeight: 850,
                  fontSize: 13,
                  height: 38,
                  px: 0.75,
                }}
              />
            ))}
            {/* IND-033: acceso a la vista «Desperdicio». */}
            <Button
              variant="outlined"
              onClick={openWaste}
              sx={{ height: 38, ml: 0.75, textTransform: 'none', fontWeight: 850, whiteSpace: 'nowrap' }}
            >
              Desperdicio
              {wasteButton.month && wasteButton.pct !== null ? (
                <Box component="span" sx={{ ml: 0.75, color: MUTED, fontWeight: 700 }}>
                  {`${wasteButton.month}. ${wastePctFormat.format(wasteButton.pct)} %`}
                </Box>
              ) : null}
              {wasteButton.missing ? (
                <Box component="span" sx={{ ml: 0.75, color: '#fbbf24', fontWeight: 800 }}>
                  {`falta ${wasteButton.missing}`}
                </Box>
              ) : null}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* TARJETAS POR MÁQUINA */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))',
            xl: 'repeat(5, minmax(0, 1fr))',
          },
          '@media (min-width: 1800px)': {
            gridTemplateColumns:
              visibleLines.length === 6
                ? 'repeat(6, minmax(0, 1fr))'
                : 'repeat(5, minmax(0, 1fr))',
          },
          gap: 1.5,
        }}
      >
        {visibleLines.map((line) => (
          <LineCard
            key={line.machine}
            line={line}
            stats={lineProductionStatsByMachine[line.machine]}
            currentYear={currentYear}
            onOpenOrders={() => setSelectedLine(line)}
          />
        ))}
      </Box>

      {/* COBERTURA DE STOCKS · BLOQUE PRINCIPAL */}
      <Paper
        sx={{
          ...PANEL,
          p: 1.75,
          minWidth: 0,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'flex-start', sm: 'baseline' }}
          justifyContent="space-between"
          sx={{ mb: 1.4 }}
        >
          <Typography sx={{ color: '#f8fafc', fontWeight: 700, fontSize: 20 }}>
            Cobertura de materiales
          </Typography>
          <Typography sx={{ color: MUTED, fontSize: 13 }}>
            {selectedGroup === 'TODAS'
              ? 'Materiales que usan las OF de todos los grupos.'
              : `Materiales que usan las OF de ${selectedGroup}.`}{' '}
            Las cantidades incluyen todas las máquinas.
          </Typography>
        </Stack>

        {stockQuery.isError ? (
          <Alert severity="warning" sx={{ mb: 1.25 }}>
            No se pudo recuperar la cobertura de materiales.
          </Alert>
        ) : null}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              // IND-015: lado a lado solo si caben sin partir el nombre en más de 2 líneas.
              xl: 'repeat(2, minmax(0, 1fr))',
            },
            gap: 1.25,
            alignItems: 'stretch',
          }}
        >
          <IndustrialCostCoverageTable
            title="Materias primas"
            rows={rawMaterialStockRows}
            calculationDate={stockQuery.data?.summary.calculation_date}
            loading={stockQuery.isLoading}
            onSelect={setSelectedMaterialCoverage}
          />

          <IndustrialCostCoverageTable
            title="Embalajes"
            rows={packagingStockRows}
            calculationDate={stockQuery.data?.summary.calculation_date}
            loading={stockQuery.isLoading}
            onSelect={setSelectedMaterialCoverage}
          />
        </Box>

        <Button
          variant="text"
          size="small"
          disabled={!stockQuery.data}
          onClick={handleDownloadStockAudit}
          sx={{ mt: 0.4, px: 0, textTransform: 'none', fontSize: 11 }}
        >
          Descargar diagnóstico de stock
        </Button>
      </Paper>

      {/* IND-027: rotación de materiales (pide sus datos aparte). */}
      <IndustrialCostMaterialRotationSection
        stockRows={stockQuery.data?.rows ?? []}
        stockReady={Boolean(stockQuery.data)}
        stockFailed={stockQuery.isError && !stockQuery.data}
        calculationDate={stockQuery.data?.summary.calculation_date}
        onSelect={setSelectedMaterialCoverage}
      />

      <IndustrialCostMaterialCoverageDialog
        material={selectedMaterialCoverage}
        calculationDate={stockQuery.data?.summary.calculation_date}
        onClose={() => setSelectedMaterialCoverage(null)}
      />

      <PlantDataNoticesDialog
        open={dataQualityDialogOpen}
        onClose={() => setDataQualityDialogOpen(false)}
        onOpenFileReview={onOpenFileReview}
      />

      <PlantDataFreshnessDialog
        open={freshnessDialogOpen}
        onClose={() => setFreshnessDialogOpen(false)}
        kpiNote={kgNote ? `${kpiNote}\n${kgNote}` : kpiNote}
        onOpenFileReview={onOpenFileReview}
      />

      {/* DETALLE DE LÍNEA */}
      <Dialog
        open={Boolean(selectedLine)}
        onClose={() => setSelectedLine(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedLine?.machine} · Órdenes pendientes
        </DialogTitle>

        <DialogContent>
          <TableContainer sx={{ maxHeight: '65vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {/* IND-015: ordenar pulsando la cabecera de cualquier columna */}
                  {ORDER_COLUMNS.map((column) => (
                    <SortableHeaderCell
                      key={column.key}
                      label={column.label}
                      columnKey={column.key}
                      sort={ordersSort}
                      align={column.align}
                      firstDirection={column.align === 'right' ? 'desc' : 'asc'}
                    />
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {sortOrdersBy(selectedLine?.orders ?? [], ordersSort.key, ordersSort.direction).map((order) => (
                  <TableRow key={order.manufacturing_order_number}>
                    <TableCell>
                      {order.manufacturing_order_number}
                    </TableCell>

                    <TableCell>{order.product_ref || '—'}</TableCell>

                    <TableCell align="right">
                      {fmt(order.pending_m)}
                    </TableCell>

                    <TableCell>
                      {fmtDate(order.dispatch_date)}
                    </TableCell>

                    <TableCell>
                      {order.customer_number || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </Stack>
  );
}